/**
 * Tiny fuzzy-match scorer for the command palette.
 *
 * Designed for command-palette latency (hundreds of items max,
 * sub-millisecond scoring). Not a full Levenshtein — that's overkill
 * for the typical "abbreviation match" pattern users actually use
 * (`gp` → "Go to Projects", `dr` → "Open Drafts").
 *
 * Algorithm:
 *
 *   1. Empty query → score 1 (everything matches equally; original
 *      order is preserved by the caller's stable sort).
 *   2. Lowercase both sides.
 *   3. Walk the haystack, advancing the needle pointer on each match.
 *      Bonus points for matches at word starts (after space / `.` /
 *      `-` / `_`) and consecutive runs.
 *   4. Score = matched-needle-chars * (1 + bonuses) / haystack-length.
 *      Returns 0 when the needle isn't fully consumed (no partial
 *      matches — keeps the result list focused).
 *
 * The implementation is deliberately allocation-free in the hot loop
 * so a 60-item list re-scoring on every keystroke stays under a frame.
 */

export function fuzzyScore(query: string, haystack: string): number {
  if (!query) return 1;
  const q = query.toLowerCase();
  const h = haystack.toLowerCase();
  let qi = 0;
  let lastMatch = -2;
  let bonus = 0;
  let matched = 0;

  for (let i = 0; i < h.length && qi < q.length; i++) {
    if (h.charCodeAt(i) === q.charCodeAt(qi)) {
      matched += 1;
      // Word-start bonus.
      if (i === 0) bonus += 1;
      else {
        const prev = h.charCodeAt(i - 1);
        // 32=space, 45=-, 46=., 47=/, 95=_
        if (
          prev === 32 ||
          prev === 45 ||
          prev === 46 ||
          prev === 47 ||
          prev === 95
        ) {
          bonus += 0.8;
        }
      }
      // Consecutive-run bonus.
      if (i === lastMatch + 1) bonus += 0.4;
      lastMatch = i;
      qi += 1;
    }
  }

  if (qi < q.length) return 0;
  return (matched * (1 + bonus)) / Math.max(h.length, 1);
}

/**
 * Score a command against a query, taking label + hint + keywords into
 * account. Returns the highest single field score so an alias-only
 * match still bubbles a command to the top.
 */
export function scoreCommand(
  query: string,
  fields: { label: string; hint?: string; keywords?: string[] },
): number {
  const fromLabel = fuzzyScore(query, fields.label);
  const fromHint = fields.hint ? fuzzyScore(query, fields.hint) * 0.7 : 0;
  let fromKw = 0;
  if (fields.keywords) {
    for (const kw of fields.keywords) {
      const s = fuzzyScore(query, kw) * 0.85;
      if (s > fromKw) fromKw = s;
    }
  }
  return Math.max(fromLabel, fromHint, fromKw);
}
